import type { HeadTag, HeadTagName } from './HeadContext.ts';
import { isPlainObject } from 'es-toolkit';
import { isValidElement, use, useEffect, useId, useMemo } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { HeadContext } from './HeadContext.ts';

export function Head({ children }: React.PropsWithChildren) {
  const { setGroup, removeGroup } = use(HeadContext);

  const id = useId();

  const tags = useMemo(() => buildHeadTags(children), [children]);

  // Immediately set the head tags if we are rendering on the server
  if (typeof window === 'undefined') {
    setGroup(id, tags);
  }

  // Remove group when component unmounts
  useEffect(() => {
    return () => {
      removeGroup(id);
    };
  }, [id, removeGroup]);

  // Update tags whenever they change
  useEffect(() => {
    setGroup(id, tags);
  }, [id, setGroup, removeGroup, tags]);

  return null;
}

function buildHeadTags(children: React.ReactNode): HeadTag[] {
  return flattenChildren(children)
    .map(child => {
      if (isValidElement(child)) {
        let props = isPlainObject(child.props) ? child.props : {};

        if (typeof window === 'undefined') {
          props = {
            ...props,
            'data-ssr': true
          };
        }

        return {
          tagName: child.type as HeadTagName,
          props
        };
      }
    })
    .filter(element => typeof element !== 'undefined');
}
